import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retry, delayWhen } from 'rxjs/operators';
import { CONFIG_SERVICE } from '@auth';

/**
 * Retry Interceptor
 * This interceptor applies retry logic for HTTP requests based on configurable criteria.
 * It handles retries for specific HTTP status codes with exponential backoff and allows
 * configuration through the injected CONFIG_SERVICE.
 */
export const retryInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  // Inject the configuration service for retry logic.
  const configService = inject(CONFIG_SERVICE, { optional: true });

  // Ensure the configuration service is properly configured.
  if (!configService?.getRetryConfig()) {
    console.error('Missing getRetryConfig() on config service, cannot load Retry Interceptor');
    return next(req);
  }

  // Extract retry configuration parameters.
  const retryConfig = configService.getRetryConfig();
  const {
    maxRetries = 3, // Maximum number of retry attempts (default: 3).
    initialRetryDelay = 500, // Initial delay before the first retry (default: 500ms).
    delayMultiplier = 2, // Multiplier for exponential backoff (default: 2).
    retryableStatusCodes = [500, 502, 503, 504], // Status codes that trigger a retry.
    logRetryAttempts = true, // Whether to log retry attempts (default: true).
    logErrors = true, // Whether to log errors (default: true).
  } = retryConfig;

  /**
   * Apply the retry logic to the HTTP request pipeline.
   * The retry logic is implemented using RxJS's `retry` operator for better control over retries.
   */
  return next(req).pipe(
    retry({
      count: maxRetries, // Maximum retries
      delay: (error, retryCount) => {
        // Retry only for specific status codes
        if (retryableStatusCodes.includes(error.status) && retryCount < maxRetries) {
          if (logRetryAttempts) {
            console.log(
              `Retrying ${req.url} (Attempt ${retryCount + 1}/${maxRetries}) due to ${error.status}`
            );
          }
          // Calculate the retry delay with exponential backoff
          const retryDelay = initialRetryDelay * Math.pow(delayMultiplier, retryCount);
          return timer(retryDelay);
        }
        // No retry if we exceed the max retries or it's a non-retryable status code.
        return throwError(() => error);
      }
    }),
    catchError((error) => {
      if (logErrors) {
        console.error(`Request to ${req.url} failed after retries.`, error);
      }
      // Pass the error downstream
      return throwError(() => error);
    })
  );
};
